interface TextStyleFlags {
  bold: boolean
  italic: boolean
}

export interface TextToken extends TextStyleFlags {
  type: 'text'
  value: string
}

export interface LinkToken extends TextStyleFlags {
  type: 'link'
  label: string
  url: string
}

export type MarkdownToken = TextToken | LinkToken

export type MarkdownBlock = ParagraphBlock | ListBlock

export interface ParagraphBlock {
  type: 'paragraph'
  content: string
}

export interface ListBlock {
  type: 'list'
  ordered: boolean
  items: string[]
}

const ESCAPABLE_MARKDOWN_CHARACTERS = new Set([
  '\\',
  '`',
  '*',
  '_',
  '{',
  '}',
  '[',
  ']',
  '(',
  ')',
  '#',
  '+',
  '-',
  '.',
  '!',
])

const MARKDOWN_LIST_ITEM_PATTERN = /^(\s*)([-*]|\d+\.)\s+(.*)$/

function isHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value)
}

function findUnescapedCharacter(
  value: string,
  target: string,
  fromIndex: number,
): number {
  for (let i = fromIndex; i < value.length; i += 1) {
    if (value[i] === '\\') {
      i += 1
      continue
    }

    if (value[i] === target) {
      return i
    }
  }

  return -1
}

function findUnescapedSubstring(
  value: string,
  target: string,
  fromIndex: number,
): number {
  for (let i = fromIndex; i <= value.length - target.length; i += 1) {
    if (value[i] === '\\') {
      i += 1
      continue
    }

    if (value.slice(i, i + target.length) === target) {
      return i
    }
  }

  return -1
}

function decodeEscapedCharacters(value: string): string {
  let result = ''

  for (let i = 0; i < value.length; i += 1) {
    const current = value[i]
    const next = value[i + 1]

    if (
      current === '\\' &&
      next != null &&
      ESCAPABLE_MARKDOWN_CHARACTERS.has(next)
    ) {
      result += next
      i += 1
      continue
    }

    result += current
  }

  return result
}

export function parseInlineMarkdown(
  value: string,
  style: TextStyleFlags = { bold: false, italic: false },
): MarkdownToken[] {
  const tokens: MarkdownToken[] = []
  let cursor = 0
  let buffer = ''

  const flushBuffer = () => {
    if (buffer.length === 0) return
    tokens.push({
      type: 'text',
      value: buffer,
      ...style,
    })
    buffer = ''
  }

  while (cursor < value.length) {
    const current = value[cursor]
    const next = value[cursor + 1]

    if (
      current === '\\' &&
      next != null &&
      ESCAPABLE_MARKDOWN_CHARACTERS.has(next)
    ) {
      buffer += next
      cursor += 2
      continue
    }

    if (current === '[') {
      const bracketEnd = findUnescapedCharacter(value, ']', cursor + 1)
      if (bracketEnd !== -1 && value[bracketEnd + 1] === '(') {
        const parenEnd = findUnescapedCharacter(value, ')', bracketEnd + 2)
        if (parenEnd !== -1) {
          const url = value.slice(bracketEnd + 2, parenEnd).trim()
          if (isHttpUrl(url)) {
            const label = decodeEscapedCharacters(
              value.slice(cursor + 1, bracketEnd),
            )
            flushBuffer()
            tokens.push({
              type: 'link',
              label,
              url,
              ...style,
            })
            cursor = parenEnd + 1
            continue
          }
        }
      }
    }

    if (value.startsWith('**', cursor) || value.startsWith('__', cursor)) {
      const marker = value.startsWith('**', cursor) ? '**' : '__'
      const markerEnd = findUnescapedSubstring(value, marker, cursor + 2)
      if (markerEnd !== -1) {
        const nestedValue = value.slice(cursor + 2, markerEnd)
        flushBuffer()
        tokens.push(
          ...parseInlineMarkdown(nestedValue, {
            ...style,
            bold: true,
          }),
        )
        cursor = markerEnd + 2
        continue
      }
    }

    if (current === '*') {
      const markerEnd = findUnescapedCharacter(value, '*', cursor + 1)
      if (markerEnd !== -1) {
        const nestedValue = value.slice(cursor + 1, markerEnd)
        flushBuffer()
        tokens.push(
          ...parseInlineMarkdown(nestedValue, {
            ...style,
            italic: true,
          }),
        )
        cursor = markerEnd + 1
        continue
      }
    }

    buffer += current
    cursor += 1
  }

  flushBuffer()

  return tokens
}

export function parseMarkdownBlocks(content: string): MarkdownBlock[] {
  const normalized = content.replace(/\r\n/g, '\n').trim()
  if (normalized.length === 0) return []

  const chunks = normalized
    .split(/\n{2,}/)
    .map((chunk) => chunk.trim())
    .filter((chunk) => chunk.length > 0)

  return chunks.map((chunk) => {
    const lines = chunk
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0)

    if (lines.length === 0) {
      return { type: 'paragraph', content: chunk }
    }

    const listMatches = lines.map((line) =>
      line.match(MARKDOWN_LIST_ITEM_PATTERN),
    )
    const isList = listMatches.every((match) => match != null)

    if (!isList) {
      return {
        type: 'paragraph',
        content: lines.join('\n'),
      }
    }

    const firstMarker = listMatches[0]?.[2] ?? '-'
    const ordered = /^\d+\.$/.test(firstMarker)

    return {
      type: 'list',
      ordered,
      items: listMatches.map((match) => match?.[3] ?? ''),
    }
  })
}
